import React, { useContext, useEffect, useRef, useState } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../components/shared/Spinner";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import {
  mdItemTrackerHeaders,
  itemTrackerHeaders,
  smItemTrackerHeaders,
} from "./utils/headers";
import { DataTableWithPagination } from "../../components/custom/DataTable/DataTableWithPagination";
import TrackerEnquiryModal from "./partials/TrackerEnquiryModal";
import { getMergedTrackerOrders } from "../../redux/dispatcher/productTrackerDispatcher";
import { useToast } from "react-native-toast-notifications";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import {
  filterSpecificField,
  resetSearch,
  searchTrackerOrders,
  sortTrackerOrdersByOrderNumber,
} from "../../redux/reducers/productTrackerReducer";
import { ItemTrackerBadgeHorizontalList } from "../../components/custom/Badge/ItemTrackerBadgeHorizontalList";
import { translateHeaders, useTranslation } from "../../utils/useTranslation";
import { customerOrderTrackerTranslationMap } from "../../utils/translations";
import TrackerHistoryModal from "./partials/TrackerHistoryModal";
import TrackerDateModal from "./partials/TrackerDateModal";

export default function ItemTrackerScreen({ navigation }: any) {
  const [modalOpen, setModalOpen] = useState<boolean>();
  const onClose = () => {
    setModalOpen(false);
  };
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);
  const isFocused = useIsFocused();
  const [isRefresh, setRefresh] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const toast = useToast();
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      itemTrackerHeaders
    );
  const { t } = useTranslation();
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);
  const columnsToNotHaveThousandsSeparator: Array<string> = ["Packing #"];
  const operationsFilterCategoryList = [
    t("all"),
    t("onHold"),
    t("newOrder"),
    t("arrived"),
    t("shipped"),
    t("rushOrder"),
    t("orderPlaced"),
    t("atRisk"),
    t("closed"),
  ];

  const dispatch = useDispatch<any>();
  const {
    loading,
    filteredOrders,
    sortDirection,
    orders,
    isProductTrackerLoaded,
  } = useSelector((state: any) => state.productTracker);

  const listRef = useRef<ScrollView>(null);
  const language = useSelector((state: any) => state.configurations.language);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };
  const [nestedTableHeaders, setNestedTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      itemTrackerHeaders
    );

  const fetchTrackerOrders = () => {
    dispatch(getMergedTrackerOrders());
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "view") {
      navigation.navigate("ProductTrackerDetailsReport", { order });
    } else {
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    }
  };

  const navigatePressAction = (filterField: string, filterValue: string) => {
    // dispatch here
    if (
      filterValue !== undefined &&
      filterValue !== null &&
      !(typeof filterValue === "string" && filterValue.trim() === "")
    ) {
      if (["OrderNumber1", "ItemName", "BPName"].includes(filterField)) {
        dispatch(
          filterSpecificField({
            filterField: filterField,
            filterValue: filterValue,
          })
        );
      } else if (
        ["OrderDate", "ReadyByDate", "ETA", "RequestDate"].includes(filterField)
      ) {
        // show calender
        setModalOpen(true);
        setSelectedItem(filterValue.trim());
        setSelectedEnquiryType("date");
      }
    }
  };

  useEffect(() => {
    const selectedHeaders = isLargeDevice
      ? itemTrackerHeaders
      : isMediumDevice
      ? mdItemTrackerHeaders
      : smItemTrackerHeaders;
    const translated = translateHeaders(
      selectedHeaders,
      t,
      customerOrderTrackerTranslationMap
    );

    setNestedTableHeaders(translated);

    if (isRefresh) {
      fetchTrackerOrders();
      setRefresh(false);
    }

    if ((!orders || orders.length === 0) && !isProductTrackerLoaded) {
      fetchTrackerOrders();
    }
  }, [isFocused, isRefresh, orders, language]);

  const handleSearch = (searchedText: string) => {
    if (searchedText) {
      dispatch(searchTrackerOrders(searchedText));
    } else {
      dispatch(resetSearch());
    }
  };

  const renderFilters = () => (
    <View
      style={[styles.renderFilterStyles, !isLargeDevice && styles.mobileLayout]}
    >
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filterContainer}
      >
        {!isSmallDevice && !isMobileDevice && (
          <Text style={styles.quickFilterText}>{t("quickFilter")}</Text>
        )}
        <ItemTrackerBadgeHorizontalList
          itemsList={operationsFilterCategoryList}
          filterCategory={selectedCategory || ""}
          setFilterCategory={setSelectedCategory!}
          tableHeaders={tableHeaders}
          trackerType="CUSTOMER"
        />
      </ScrollView>

      <LabelBannerWithSearchBar
        titleName={`${t("totalItems")}: ${filteredOrders.length}`}
        handleSearch={handleSearch}
        setRefresh={setRefresh}
        dataToExport={filteredOrders}
        fileName="ItemTrackerScreen"
        sheetName="Item Tracker Report Screen"
      />
    </View>
  );

  const onHandleSortColumn = (columnKey: string) => {
    if (sortDirection === "desc") {
      dispatch(sortTrackerOrdersByOrderNumber("asc"));
    } else {
      dispatch(sortTrackerOrdersByOrderNumber("desc"));
    }
  };

  return (
    <ScrollView
      scrollEventThrottle={16}
      onScroll={handleScroll}
      ref={listRef}
      style={styles.container}
    >
      <View style={styles.filterMainContainer}>{renderFilters()}</View>
      {modalOpen && selectedEnquiryType === "add" && (
        <TrackerEnquiryModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "history" && (
        <TrackerHistoryModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "date" && (
        <TrackerDateModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      <View style={styles.contentContainer}>
        {loading ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff" }}>
            <DataTableWithPagination
              isToExpandAllRows={false}
              isRowsExpandable={false}
              headers={nestedTableHeaders}
              itemsPerPage={20}
              isNestedDataLoaded={loading}
              nestedHeader={nestedTableHeaders}
              rows={filteredOrders}
              key="rowKeyValue"
              rowKeyValue="OrderNumber1"
              columnsToNotHaveThousandSeparator={
                columnsToNotHaveThousandsSeparator
              }
              handleSortColumn={onHandleSortColumn}
              operationActions={navigateToOrder}
              itemTracker={true}
              // Required props
              useModal={false}
              setModalVisible={() => {}}
              handleRowClick={() => {}}
              setSelected={() => {}}
              handleDeleteRow={() => {}}
              navigatePressAction={navigatePressAction}
            />
          </View>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 20,
    paddingHorizontal: 15,
    backgroundColor: "#fff",
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#000",
  },
  newOrderButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#1E88E5",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  newOrderText: {
    color: "#fff",
    marginRight: 5,
    fontWeight: "bold",
    cursor: "pointer",
  },
  filterMainContainer: {
    width: "100%",
  },
  filterContainer: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  operationsContainer: {
    padding: 15,
    backgroundColor: "#fff",
  },
  operationCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#fff",
    padding: 15,
    borderRadius: 12,
    marginBottom: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  operationCardContent: {
    flex: 1,
  },
  operationTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
  },
  operationStatus: {
    fontSize: 14,
    color: "#555",
  },
  contentContainer: {
    flex: 1,
    backgroundColor: "#fff",
  },
  spinnerContainer: {
    position: "absolute",
    top: 100,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
  },
  quickFilterText: {
    display: "flex",
    alignItems: "center",
    marginRight: 10,
  },
  renderFilterStyles: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 10,
  },
  mobileLayout: {
    flexDirection: "column",
  },
  fab: {
    position: "absolute",
    margin: 16,
    right: 0,
    zIndex: 1,
  },
});
